import React, { useState, useEffect, useCallback } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { Box, Typography, CircularProgress, Alert, TextField, Select, MenuItem } from "@mui/material";
import {
  fetchTestBank,
  createTestBankItem,
  updateTestBankItem,
  deleteTestBankItem,
} from "../../api/backend";
import { useActiveTenant } from "../../hooks/useActiveTenant";
import { useConfigList } from "../../hooks/useConfigList";
import NoTenantSelected from "../../components/NoTenantSelected";

const DEFAULT_CATEGORIES = ["Firefighter", "EMS", "Hazmat", "Search & Rescue", "Fire Officer", "General"];

const D = {
  ink: "var(--color-ink)", ink2: "var(--color-ink-2)", ink3: "var(--color-ink-3)", ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)", line2: "var(--color-surface-press-light)", surface2: "var(--color-surface-2)", surface3: "var(--color-surface-press-light)",
  danger: "#B42318", danger50: "rgba(180,35,24,.08)", danger100: "rgba(180,35,24,.18)",
  shadowSm: "0 1px 0 rgba(20,24,40,.04),0 1px 3px rgba(20,24,40,.06)",
};

function SvgIcon({ size = 15, sw = 2, children }) {
  return <Box component="svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">{children}</Box>;
}

function makeCSx(tPrimary) {
  return { "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: D.surface2, "& fieldset": { borderColor: D.line, borderWidth: "1.5px" }, "&:hover fieldset": { borderColor: D.ink3 }, "&.Mui-focused fieldset": { borderColor: tPrimary, borderWidth: "1.5px" }, "&.Mui-focused": { bgcolor: "#fff" } }, "& .MuiInputBase-input": { fontSize: "14px", fontWeight: 500, color: D.ink } };
}
function makeMSx(tPrimary) {
  const csx = makeCSx(tPrimary);
  return { ...csx, "& .MuiOutlinedInput-root": { ...csx["& .MuiOutlinedInput-root"], height: "auto" } };
}

function Btn({ children, primary, danger, ghost, sm, onClick, disabled, icon }) {
  const { palette } = useTheme();
  const tPrimary = palette.primary.main;
  const tPrimaryDk = palette.primary.dark;
  const h = sm ? 32 : 38; const px = sm ? "13px" : "18px"; const fs = sm ? "12.5px" : "13px";
  const base = { height: h, px, borderRadius: sm ? "9px" : "11px", border: 0, fontFamily: "inherit", fontSize: fs, fontWeight: 700, cursor: disabled ? "default" : "pointer", display: "inline-flex", alignItems: "center", gap: "7px", transition: "all .13s", whiteSpace: "nowrap" };
  const v = primary ? { bgcolor: disabled ? D.surface3 : tPrimary, color: disabled ? D.ink3 : "#fff", boxShadow: disabled ? "none" : `0 4px 12px -4px ${alpha(tPrimary, 0.45)}`, "&:hover:not(:disabled)": { bgcolor: tPrimaryDk } }
    : danger ? { bgcolor: D.danger50, color: D.danger, border: `1.5px solid ${D.danger100}`, "&:hover": { bgcolor: D.danger100 } }
    : { bgcolor: D.surface2, color: D.ink2, border: `1.5px solid ${D.line}`, "&:hover": { bgcolor: D.surface3 } };
  return <Box component="button" onClick={onClick} disabled={disabled} sx={{ ...base, ...v }}>{icon}{children}</Box>;
}

function parseTags(raw = "[]") {
  try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return raw.split(",").map(s => s.trim()).filter(Boolean); }
}

const EMPTY = { title: "", description: "", questions: "[]", passingScore: 80, timeLimitMin: "", maxRetakes: 0, tags: "" };

function TestForm({ initial, onSave, onCancel, saving }) {
  const { palette } = useTheme();
  const cSx = makeCSx(palette.primary.main);
  const mSx = makeMSx(palette.primary.main);
  const CATEGORIES = useConfigList("SKILL_CATEGORY", DEFAULT_CATEGORIES);
  const [form, setForm] = useState(initial || EMPTY);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const tagList = parseTags(typeof form.tags === "string" ? form.tags : form.tags.join(", "));
  const currentCategory = tagList.find(t => CATEGORIES.includes(t)) || "";
  const otherTags = tagList.filter(t => !CATEGORIES.includes(t));
  const setCategory = (e) => {
    const newCat = e.target.value;
    setForm(p => ({ ...p, tags: (newCat ? [newCat, ...otherTags] : otherTags).join(", ") }));
  };
  const setOtherTags = (e) => {
    const parsed = parseTags(e.target.value);
    setForm(p => ({ ...p, tags: (currentCategory ? [currentCategory, ...parsed] : parsed).join(", ") }));
  };
  const save = () => onSave({
    ...form,
    passingScore: parseInt(form.passingScore) || 80,
    timeLimitMin: form.timeLimitMin ? parseInt(form.timeLimitMin) : null,
    maxRetakes: parseInt(form.maxRetakes) || 0,
    tags: JSON.stringify(parseTags(typeof form.tags === "string" ? form.tags : form.tags.join(", "))),
  });

  const fl = (label, opt) => (
    <Box sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: D.ink3, mb: "7px" }}>
      {label}{opt && <Box component="span" sx={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: D.ink4, ml: "4px" }}>— optional</Box>}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", p: "20px" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "14px" }}>
        <Box>{fl("Title")}<TextField fullWidth size="small" value={form.title} onChange={set("title")} sx={{ ...cSx, "& .MuiOutlinedInput-root": { ...cSx["& .MuiOutlinedInput-root"], height: 44 } }} /></Box>
        <Box>{fl("Category", true)}
          <Select fullWidth size="small" displayEmpty value={currentCategory} onChange={setCategory} sx={{ ...cSx, "& .MuiOutlinedInput-root": { ...cSx["& .MuiOutlinedInput-root"], height: 44 } }}>
            <MenuItem value=""><em>None</em></MenuItem>
            {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </Box>
      </Box>
      <Box>{fl("Tags", true)}<TextField fullWidth size="small" placeholder="ems, cpr, scenario" value={otherTags.join(", ")} onChange={setOtherTags} sx={{ ...cSx, "& .MuiOutlinedInput-root": { ...cSx["& .MuiOutlinedInput-root"], height: 44 } }} /></Box>
      <Box>{fl("Description", true)}<TextField fullWidth multiline rows={2} value={form.description || ""} onChange={set("description")} sx={mSx} /></Box>
      <Box>{fl("Questions (JSON array)")}<TextField fullWidth multiline rows={5} placeholder='[{"text":"Question 1","options":["A","B","C","D"],"correct":0,"points":10},...]' value={form.questions || "[]"} onChange={set("questions")} sx={mSx} inputProps={{ style: { fontFamily: "monospace", fontSize: "13px" } }} /></Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "14px" }}>
        <Box>{fl("Passing Score (%)")}<TextField fullWidth size="small" type="number" value={form.passingScore} onChange={set("passingScore")} sx={{ ...cSx, "& .MuiOutlinedInput-root": { ...cSx["& .MuiOutlinedInput-root"], height: 44 } }} /></Box>
        <Box>{fl("Time Limit (min)", true)}<TextField fullWidth size="small" type="number" value={form.timeLimitMin || ""} onChange={set("timeLimitMin")} sx={{ ...cSx, "& .MuiOutlinedInput-root": { ...cSx["& .MuiOutlinedInput-root"], height: 44 } }} /></Box>
        <Box>{fl("Max Retakes")}<TextField fullWidth size="small" type="number" value={form.maxRetakes} onChange={set("maxRetakes")} sx={{ ...cSx, "& .MuiOutlinedInput-root": { ...cSx["& .MuiOutlinedInput-root"], height: 44 } }} /></Box>
      </Box>
      <Box sx={{ display: "flex", gap: "9px", justifyContent: "flex-end" }}>
        <Btn ghost sm onClick={onCancel}>Cancel</Btn>
        <Btn primary sm onClick={save} disabled={!form.title || saving}>{saving ? "Saving…" : "Save to Bank"}</Btn>
      </Box>
    </Box>
  );
}

export default function TestBankPage() {
  const { palette } = useTheme();
  const tPrimary = palette.primary.main;
  const tAccent = palette.warning.main;
  const { tenantId: activeTenantId, isGlobalAdmin } = useActiveTenant();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [err, setErr] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteErr, setDeleteErr] = useState(null);

  const load = useCallback(() => {
    if (isGlobalAdmin && !activeTenantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchTestBank(search || undefined).then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); }).catch(() => { setErr("Failed to load."); setLoading(false); });
  }, [search, activeTenantId, isGlobalAdmin]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => { setSaving(true); try { const item = await createTestBankItem(form); setItems(p => [item, ...p]); setShowCreate(false); } catch { setErr("Failed to create."); } finally { setSaving(false); } };
  const handleUpdate = async (id, form) => { setSaving(true); try { const u = await updateTestBankItem(id, form); setItems(p => p.map(i => i.id === id ? u : i)); setEditId(null); } catch { setErr("Failed to update."); } finally { setSaving(false); } };
  const handleDelete = async (id) => { setDeleteErr(null); try { await deleteTestBankItem(id); setItems(p => p.filter(i => i.id !== id)); setDeleteId(null); } catch (e) { setDeleteErr(e?.response?.data?.message || "Cannot delete — may be in use."); } };

  const PanelHead = ({ title, icon }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: "11px", px: "20px", py: "13px", borderBottom: `1px solid ${D.line2}` }}>
      <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: alpha(tPrimary, 0.06), color: tPrimary, display: "grid", placeItems: "center" }}>{icon}</Box>
      <Typography sx={{ fontSize: "15px", fontWeight: 700, color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>{title}</Typography>
    </Box>
  );

  if (isGlobalAdmin && !activeTenantId) {
    return (
      <Box sx={{ maxWidth: 1160, mx: "auto" }}>
        <NoTenantSelected message="Select a training center from the top bar to view its test bank." />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1160, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "20px", flexWrap: "wrap", gap: "12px" }}>
        <Box>
          <Typography sx={{ fontSize: "26px", fontWeight: 800, letterSpacing: "-.03em", color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>Test Bank</Typography>
          <Box sx={{ fontSize: "13.5px", color: D.ink3, mt: "4px" }}>Reusable written tests — assign to any course.</Box>
        </Box>
        <Btn primary onClick={() => { setShowCreate(true); setEditId(null); }} icon={<SvgIcon size={13} sw={2.5}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></SvgIcon>}>New Test</Btn>
      </Box>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2, borderRadius: "12px" }}>{err}</Alert>}

      <Box sx={{ bgcolor: "#fff", border: `1px solid ${D.line}`, borderRadius: "12px", px: "14px", display: "flex", alignItems: "center", gap: "10px", height: 44, mb: "16px", maxWidth: 380 }}>
        <SvgIcon size={14}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></SvgIcon>
        <Box component="input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tests…" sx={{ flex: 1, border: 0, outline: 0, bgcolor: "transparent", fontSize: "14px", color: D.ink }} />
      </Box>

      {showCreate && (
        <Box sx={{ bgcolor: "#fff", border: `1px solid ${D.line}`, borderRadius: "16px", boxShadow: D.shadowSm, overflow: "hidden", mb: "16px" }}>
          <PanelHead title="New Test" icon={<SvgIcon><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></SvgIcon>} />
          <TestForm onSave={handleCreate} onCancel={() => setShowCreate(false)} saving={saving} />
        </Box>
      )}

      {loading ? <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: tPrimary }} /></Box>
        : items.length === 0 ? <Box sx={{ bgcolor: "#fff", border: `1px solid ${D.line}`, borderRadius: "16px", p: "40px", textAlign: "center", color: D.ink4, fontSize: "13.5px" }}>{search ? "No tests match." : "No tests in the bank yet."}</Box>
        : <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {items.map(item => (
            <Box key={item.id} sx={{ bgcolor: "#fff", border: `1px solid ${D.line}`, borderRadius: "16px", boxShadow: D.shadowSm, overflow: "hidden" }}>
              {editId === item.id ? (
                <>
                  <PanelHead title={`Edit: ${item.title}`} icon={<SvgIcon><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></SvgIcon>} />
                  <TestForm initial={{ ...item, tags: parseTags(item.tags).join(", ") }} onSave={f => handleUpdate(item.id, f)} onCancel={() => setEditId(null)} saving={saving} />
                </>
              ) : (
                <Box sx={{ p: "16px 20px" }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "9px", mb: "4px", flexWrap: "wrap" }}>
                        <Box sx={{ fontSize: "15px", fontWeight: 700, color: D.ink }}>{item.title}</Box>
                        {item.isSystem && <Box component="span" sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", px: "8px", py: "2px", borderRadius: "999px", bgcolor: alpha(tAccent, 0.1), color: tAccent }}>System</Box>}
                      </Box>
                      {item.description && <Box sx={{ fontSize: "13px", color: D.ink3, mb: "8px" }}>{item.description}</Box>}
                      <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                        <Box sx={{ fontSize: "12px", color: D.ink4 }}>Passing: {item.passingScore}%</Box>
                        {item.timeLimitMin && <Box sx={{ fontSize: "12px", color: D.ink4 }}>· {item.timeLimitMin} min</Box>}
                        <Box sx={{ fontSize: "12px", color: D.ink4 }}>· Max retakes: {item.maxRetakes}</Box>
                        {parseTags(item.tags).map(t => <Box key={t} component="span" sx={{ fontSize: "11px", fontWeight: 700, px: "8px", py: "3px", borderRadius: "999px", bgcolor: alpha(tPrimary, 0.06), color: tPrimary }}>{t}</Box>)}
                      </Box>
                    </Box>
                    {!item.isSystem && (
                      <Box sx={{ display: "flex", gap: "6px" }}>
                        <Box component="button" onClick={() => setEditId(item.id)} sx={{ width: 32, height: 32, borderRadius: "8px", border: `1.5px solid ${D.line}`, bgcolor: "#fff", display: "grid", placeItems: "center", cursor: "pointer", color: D.ink3, "&:hover": { bgcolor: D.surface3 } }}>
                          <SvgIcon size={13}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></SvgIcon>
                        </Box>
                        <Box component="button" onClick={() => { setDeleteId(item.id); setDeleteErr(null); }} sx={{ width: 32, height: 32, borderRadius: "8px", border: `1.5px solid ${D.line}`, bgcolor: "#fff", display: "grid", placeItems: "center", cursor: "pointer", color: D.ink3, "&:hover": { bgcolor: D.danger50, color: D.danger, borderColor: D.danger100 } }}>
                          <SvgIcon size={13}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></SvgIcon>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  {deleteId === item.id && (
                    <Box sx={{ mt: "12px", p: "12px 14px", bgcolor: D.danger50, border: `1.5px solid ${D.danger100}`, borderRadius: "10px" }}>
                      {deleteErr && <Box sx={{ fontSize: "12.5px", color: D.danger, mb: "8px" }}>{deleteErr}</Box>}
                      <Box sx={{ fontSize: "13.5px", fontWeight: 600, color: D.danger, mb: "10px" }}>Delete "{item.title}"?</Box>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Btn ghost sm onClick={() => setDeleteId(null)}>Cancel</Btn>
                        <Btn danger sm onClick={() => handleDelete(item.id)}>Delete</Btn>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ))}
        </Box>}
    </Box>
  );
}
